import { useCallback, useSyncExternalStore } from 'react';

/**
 * 좁은 폭(<= 999px)에서 쓰는 사이드바 Drawer 의 열림 상태.
 *
 * 왜 모듈 레벨 상태인가:
 *   햄버거 버튼은 DashboardHeader, 메뉴 본문(Drawer)은 AppSidebar 가 그린다. 둘 다
 *   MainApp 이 따로 렌더하므로 열림 상태를 props 로 넘길 수 없고, Context Provider 를
 *   새로 끼우려면 MainApp(이 작업 범위 밖)을 고쳐야 한다. 그래서 구독 가능한 작은
 *   외부 스토어 하나로 두 컴포넌트가 같은 값을 본다.
 *
 * 열림 상태는 레이아웃 전용 UI 상태라 어디에도 저장하지 않는다(새로고침하면 닫힌 상태).
 *
 * 격리(2026-10-20 리뷰): 모듈 전역이라 로그아웃 뒤에도 값이 남아, 좁은 폭에서 다시 로그인하면
 * 메뉴가 열린 채로 시작하거나 테스트끼리 상태가 새는 문제가 있었다. `closeSidebarDrawer()` 로
 * 밖에서도 강제로 닫을 수 있게 해 로그아웃·테스트 정리 시 초기화한다.
 */
let drawerOpen = false;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => drawerOpen;

const setDrawerOpen = (next: boolean) => {
  if (drawerOpen === next) return;
  drawerOpen = next;
  listeners.forEach((listener) => listener());
};

/**
 * 컴포넌트 밖에서도 Drawer 를 닫는다 — 로그아웃 직후(다음 로그인에 열린 채로 남지 않게),
 * 테스트 정리(beforeEach 격리)에서 쓴다.
 */
export const closeSidebarDrawer = () => setDrawerOpen(false);

interface SidebarDrawerState {
  /** Drawer 가 열려 있는지. */
  drawerOpen: boolean;
  /** 헤더 햄버거 버튼이 호출한다. */
  openDrawer: () => void;
  /** Drawer 바깥 클릭·메뉴 선택 시 닫는다. */
  closeDrawer: () => void;
}

/**
 * 사이드바 Drawer 열림 상태 훅.
 *
 * @example
 * const { drawerOpen, openDrawer, closeDrawer } = useSidebarDrawer();
 */
export const useSidebarDrawer = (): SidebarDrawerState => {
  const isOpen = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  return { drawerOpen: isOpen, openDrawer, closeDrawer };
};
